// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';
import moment from 'moment';

// Internal Dependencies
import Colours from '../../styles/colours';
import MText from '../common/MText';
import apiService from '../../services.js/apiService';
import { IsArray, IsEmpty } from '../../common/checks';
import { Status } from '../../common/constants';
import PhotoDetailsModal from './PhotoDetailsModal';
import { formatDateToMonthDayYearHourMinute } from '../../common/util';
import ProgressiveImage from '../common/ProgressiveImage';
import Convert from '../../common/convert';
import useDebounce from '../global/use-debounce';

const Snapstyr = (props) => {
  const All = 'All';

  const [photos, setPhotos] = useState([]);
  const [selectedPhotoId, setSelectedPhotoId] = useState(null);
  const [filters, setFilters] = useState({
    poiId:   '',
    photoId: '',
    status:  All,
  });
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageNo:   0,
    pageSize: 10,
  });
  const debouncedFilters = useDebounce(filters, 750);
  const fetchPhotos = async () => {
    try {
      setLoading(true);

      // Reason + 1 is pageNo start with 0
      const params = {
        photoId: debouncedFilters.photoId,
        poiId:   debouncedFilters.poiId,
        status:  filters.status === All
          ? ''
          : filters.status,
        pageNo:   pagination.pageNo,
        pageSize: pagination.pageSize,
        pageId:   props.match.params.id,
      };

      const result = await apiService.getPhotos(
        params,
      );

      const photoData = IsArray(result.rows) ? result.rows : [];

      setPhotos(photoData);
      setTotalCount(result.totalCount || 0);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [
    debouncedFilters.photoId,
    debouncedFilters.poiId,
    filters.status,
    pagination.pageNo,
    pagination.pageSize,
  ]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, pageNo: page }));
  };

  const handleRowsPerPageChange = (pageSize) => {
    setPagination({ pageNo: 0, pageSize });
  };

  const handleViewPhoto = (photo) => {
    setSelectedPhotoId(photo.id);
  };

  const handleFilterChange = (key, value) => {
    setPagination((prev) => ({ ...prev, pageNo: 0 }));
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const columns = [
    {
      title:     'Id',
      field:     'id',
      filtering: false,
      cellStyle: {
        width:     '1%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <Box
          onClick={() => handleViewPhoto(rowData)}
        >
          <MText text={rowData.id} style={{ cursor: 'pointer', color: Colours.Blue }} />
        </Box>
      ),
    },
    {
      title:     'Photo',
      field:     'display',
      filtering: false,
      cellStyle: {
        width:     '10%',
        textAlign: 'center',
        cursor:    'pointer',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <Box
          onClick={() => handleViewPhoto(rowData)}
          sx={{
            cursor:    'pointer',
            '&:hover': {
              opacity: 0.7,
            },
            transition: 'opacity 0.2s',
          }}
        >
          <ProgressiveImage
            image={Convert.imageResizeUrl(rowData.photoUrl, 100, 100)}
            width="100%"
            height="auto"
            draggable={false}
          />
        </Box>
      ),
    },
    {
      title:     'POI Id',
      field:     'poiId',
      filtering: false,
      cellStyle: {
        width:     '5%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <a target="_blank" href={`/admin/poi/${rowData.poiId}`} rel="noreferrer">
          {rowData.poiId}
        </a>
      ),
    },
    {
      title:     'Status',
      field:     'status',
      filtering: false,
      cellStyle: {
        width:     '12%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <Chip
          label={rowData.status}
          style={{
            backgroundColor: rowData.status === Status.Approved ? Colours.Green1
              : rowData.status === Status.Rejected ? Colours.Red
                : Colours.Orange,
            color: 'white',
          }}
          size="small"
        />
      ),
    },
    {
      title:     'Taken On',
      field:     'info.takenOn',
      filtering: false,
      cellStyle: {
        width:     '5%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <MText style={{ whiteSpace: 'pre-wrap' }} text={IsEmpty(rowData.info.takenOn) ? '-' : moment(rowData.info.takenOn).format('MMMM, DD YYYY')} />
      ),
    },
    {
      title:     'Description',
      field:     'info.text',
      filtering: false,
      cellStyle: {
        width: '15%',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <MText
          style={{
            whiteSpace:      'pre-wrap',
            overflow:        'hidden',
            display:         '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 6,
          }}
          text={rowData.info.text}
        />
      ),
    },
    {
      title:     'Created At',
      field:     'createdAt',
      filtering: false,
      cellStyle: {
        width:     '3%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <MText text={formatDateToMonthDayYearHourMinute(rowData.createdAt)} />
      ),
    },
    {
      title:     'Updated At',
      field:     'updatedAt',
      cellStyle: {
        width:     '3%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <MText text={formatDateToMonthDayYearHourMinute(rowData.updatedAt)} />
      ),
    },
    {
      title:     'Updated By',
      field:     'lastUpdatedBy',
      cellStyle: {
        width:     '3%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
  ];

  return (
    <div>
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="end"
        m={0}
        p={1}
        bgcolor="background.paper"
      >
        <Box p={1} width="17%">
          <FormControl fullWidth>
            <TextField
              label="Photo Id"
              type="number"
              value={filters.photoId || ''}
              onChange={(e) => handleFilterChange('photoId', e.target.value)}
              fullWidth
            />
          </FormControl>
        </Box>

        <Box p={1} width="17%">
          <FormControl fullWidth>
            <TextField
              label="POI Id"
              type="number"
              value={filters.poiId || ''}
              onChange={(e) => handleFilterChange('poiId', e.target.value)}
              fullWidth
            />
          </FormControl>
        </Box>

        <Box p={1} width="17%">
          <FormControl fullWidth>
            <InputLabel id="status-filter-label">
              Status
            </InputLabel>

            <Select
              labelId="status-filter-label"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value={All}>All</MenuItem>
              <MenuItem value={Status.Pending}>
                {Status.Pending}
              </MenuItem>
              <MenuItem value={Status.Approved}>
                {Status.Approved}
              </MenuItem>
              <MenuItem value={Status.Rejected}>
                {Status.Rejected}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <MaterialTable
        className="material-table"
        title=""
        columns={columns}
        data={photos}
        isLoading={loading}
        page={pagination.pageNo}
        totalCount={totalCount}
        options={{
          pageSize:            pagination.pageSize,
          pageSizeOptions:     [10, 25, 50],
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
          showTitle:           false,
          tableLayout:         'auto',
          headerStyle:         {
            backgroundColor: Colours.White,
            fontWeight:      'bold',
          },
        }}
        localization={{
          body: {
            emptyDataSourceMessage: (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <MText variant="body1" colour="textSecondary" text="No photos found" />
              </Box>
            ),
          },
        }}
        onChangePage={(page) => handlePageChange(page)}
        onChangeRowsPerPage={(pageSize) => handleRowsPerPageChange(pageSize)}
      />

      <PhotoDetailsModal
        open={!IsEmpty(selectedPhotoId)}
        photoId={selectedPhotoId}
        onClose={() => {
          setSelectedPhotoId(null);
        }}
        pageId={props.match.params.id}
        onMutationComplete={() => {
          setSelectedPhotoId(null);
          fetchPhotos();
        }}
      />
    </div>
  );
};

Snapstyr.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default Snapstyr;
